import { navigateTo, simpleRoute } from '../../common/frontend/route-utils.js'
import { t } from '../../common/i18n.js'
import { Spinner } from '../components/spinner/spinner.js'
import { MenuItem, View } from '../types.js'
import { loadMyProfileIfNeeded, loadWorktimeProjectsIfNeeded } from '../utils/load-utils.js'
import {
    applySearch,
    filterMyProjects,
    groupProjectsByYear,
} from '../views/playtime/worktime/worktime-helpers.js'
import type { WorktimeViewProps } from '../views/playtime/worktime/worktime.js'

// ── Model ────────────────────────────────────────────────────────────────────

class WorktimeModel {
    // Legacy redirect — worktime now lives under the /me namespace.
    public route = simpleRoute<View>('/worktime', {
        onEnter: () => navigateTo('/me/worktime'),
        render: () => <Spinner />,
    })

    public menuItem: MenuItem = {
        text: t.playtime.worktime,
        url: this.route.toUrl(),
        matchingRoutes: [this.route],
    }

    public getProps(
        view: View,
        overrides?: {
            isPublicProfile?: boolean
            userName?: string
            userEmail?: string
        },
    ): WorktimeViewProps {
        const { state } = view
        const wt = state.worktime

        // Trigger data loading (idempotent, no-ops once loaded)
        const projectsState = loadWorktimeProjectsIfNeeded(view)
        const meData = loadMyProfileIfNeeded(view)

        const projects = projectsState.data ?? []
        const userEmail = overrides?.userEmail ?? meData.data?.email ?? null
        // Public-profile callers pass an explicit email — match only on it.
        const userId = overrides?.userEmail ? null : (meData.data?.userId ?? null)

        // Derive filtered project lists (the API sort order — newest first — is kept)
        const myProjects = filterMyProjects(projects, userEmail, userId)
        const displayedProjects = wt.activeTab === 'mine' ? myProjects : projects
        const filteredProjects = applySearch(displayedProjects, wt.searchQuery)

        // Group by year for the timeline, newest year first
        const projectsByYear = groupProjectsByYear(filteredProjects)
        const sortedYears = [...projectsByYear.keys()].sort((a, b) => Number(b) - Number(a))

        return {
            view,
            lang: state.lang,
            isPublicProfile: overrides?.isPublicProfile,
            userName: overrides?.userName,
            loading: projectsState.isLoading && !projectsState.data,
            loadError:
                projectsState.failCount > 0 && !projectsState.data
                    ? t.playtime.worktimeView.loadError(state.lang)
                    : null,
            filteredProjects,
            projectsByYear,
            sortedYears,
            activeTab: wt.activeTab,
            searchQuery: wt.searchQuery,
            selectedProject: wt.selectedProject,

            // Callbacks
            onSetActiveTab: (tab) => {
                state.worktime.activeTab = tab
                view.update()
            },
            onSetSearchQuery: (query) => {
                state.worktime.searchQuery = query
                view.update()
            },
            onProjectClick: (project) => {
                state.worktime.selectedProject = project
                view.update()
            },
            onCloseModal: () => {
                state.worktime.selectedProject = null
                view.update()
            },
        }
    }
}

export const worktimeModel = new WorktimeModel()
